import React, { useContext, useEffect, useMemo, useState } from "react";
import "../../Tabels/Tabels.css";
import "./Salesanalytics.css";
import Header from "../../Header/Header";

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";

import toast, { Toaster } from "react-hot-toast";
import { Row, Col, Table, Form } from "react-bootstrap";
import { Context } from "../../../utils/context";

import moment from "moment";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

library.add(fas);



const CATEGORIES = [
  "Spectacle Lenses",
  "Sunglasses",
  "Spectacle Frames",
  "Contact Lenses",
];

const CATEGORY_COLORS = {
  "Spectacle Lenses": "#4C6EF5",
  Sunglasses: "#F59F00",
  "Spectacle Frames": "#12B886",
  "Contact Lenses": "#E64980",
};

const GRAY_COLOR = "#adb5bd";



const getCurrentFinancialYear = () => {
  const now = moment();
 
  return now.month() < 3 ? now.year() - 1 : now.year();
};

const buildFinancialYearMonths = (fyStartYear) => {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const dt = moment({ year: fyStartYear, month: 3, day: 1 }).add(i, "months");
    months.push({ key: dt.format("YYYY-MM"), label: dt.format("MMM YYYY") });
  }
  return months;
};

const getFinancialYearOptions = (yearsBack = 6, yearsAhead = 0) => {
  const currentFY = getCurrentFinancialYear();
  const options = [];
  for (let fy = currentFY + yearsAhead; fy >= currentFY - yearsBack; fy--) {
    options.push({ value: fy, label: `FY ${fy}-${String(fy + 1).slice(-2)}` });
  }
  return options;
};


const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const values = Object.values(value);
    if (values.length && values.every((v) => v && typeof v === "object")) return values;
  }
  return [];
};



const SalesAnalytics = () => {
  const { getData } = useContext(Context);

  // ---- category filter cards — single select ------------------------------------------
  const [selectedCategory, setSelectedCategory] = useState(null);
  const handleCategoryCardClick = (label) => {
    setSelectedCategory((prev) => (prev === label ? null : label));
  };

 
  const [selectedYear, setSelectedYear] = useState(getCurrentFinancialYear());
  const yearOptions = useMemo(() => getFinancialYearOptions(6, 0), []);


  const [apiCategories, setApiCategories] = useState([]); // raw list from /common/masters, used only to map real ids

  // ---- sales chart -----------------------------------------------------------------
  const [salesSummary, setSalesSummary] = useState([]);
  const [summaryUnavailable, setSummaryUnavailable] = useState(false);
 
  const [chartClick, setChartClick] = useState(null); // { category, month } | null

  // ---- sales details table -----------------------------------------------------------------
  const [salesDetails, setSalesDetails] = useState([]);
  const [detailsUnavailable, setDetailsUnavailable] = useState(false);

  // ---- invoice expand/collapse -------------------------------------------------------
 
  const [expandedRowKey, setExpandedRowKey] = useState(null); // `${poId}-${productId}-${idx}` | null
  const [expandedRows, setExpandedRows] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const monthWindow = useMemo(() => buildFinancialYearMonths(selectedYear), [selectedYear]);

 
  const matchedCategories = useMemo(() => {
    const apiCats = Array.isArray(apiCategories) ? apiCategories : [];
    return CATEGORIES.map((targetLabel) => {
      const found = apiCats.find(
        (c) => String(c.label || "").trim().toLowerCase() === targetLabel.toLowerCase()
      );
      return { label: targetLabel, value: found?.value ?? null };
    });
  }, [apiCategories]);

  const categoryIdFor = (label) => {
    if (!label) return null;
    return matchedCategories.find((c) => c.label === label)?.value ?? null;
  };

  const getAllCategories = async () => {
    try {
      const response = await getData("/common/masters/all-vendor-p-categories");
      if (response?.success) {
        const opts = toArray(response?.data)
          .map((c) => ({ value: c?.id ?? c?.p_category_id ?? c?.value, label: c?.name ?? c?.label }))
          .filter((c) => c.value != null && c.label);
        setApiCategories(opts);
      } else {
        setApiCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setApiCategories([]);
    }
  };

  useEffect(() => {
    getAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  // SALES SUMMARY (chart data) —

  const getSalesSummaryData = async () => {
    try {
      const params = new URLSearchParams();
      params.append("year", selectedYear);

      const response = await getData(`/admin/dashboard/sales-summary?${params.toString()}`);

      if (response?.success) {
        setSalesSummary(toArray(response?.data));
        setSummaryUnavailable(false);
      } else {
        setSalesSummary([]);
        setSummaryUnavailable(true);
      }
    } catch (error) {
      console.warn("Sales summary endpoint unavailable:", error);
      setSalesSummary([]);
      setSummaryUnavailable(true);
    }
  };

  useEffect(() => {
    getSalesSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  
  useEffect(() => {
    setChartClick(null);
  }, [selectedCategory, selectedYear]);

  const chartCategories = CATEGORIES;

  const highlightCategory = chartClick?.category || selectedCategory || null;

  
  const chartData = useMemo(() => {
    return monthWindow.map(({ key, label }) => {
      const row = { month: key, label };
      chartCategories.forEach((catLabel) => {
        const match = salesSummary.find(
          (r) =>
            r?.order_month === key &&
            String(r?.["category name"] || "").trim().toLowerCase() === catLabel.toLowerCase()
        );
        row[catLabel] = match ? parseFloat(match?.total_quantity || 0) || 0 : 0;
      });
      return row;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesSummary, monthWindow]);

  const categoryYearTotals = useMemo(() => {
    return CATEGORIES.map((label) => ({
      label,
      total: Math.round(chartData.reduce((sum, row) => sum + (parseFloat(row[label]) || 0), 0) * 100) / 100,
    }));
  }, [chartData]);

  
  // SALES DETAILS TABLE 

  const effectiveCategory = chartClick?.category || selectedCategory || null;
  const effectiveMonth = chartClick?.month || null;

  const getSalesDetailsData = async () => {
    try {
      const categoryId = categoryIdFor(effectiveCategory);
      const params = new URLSearchParams();
      params.append("year", selectedYear);
      if (categoryId != null) params.append("category_id", categoryId);

      const response = await getData(`/admin/dashboard/sales-details?${params.toString()}`);

      if (response?.success) {
        setSalesDetails(toArray(response?.data));
        setDetailsUnavailable(false);
      } else {
        setSalesDetails([]);
        setDetailsUnavailable(true);
      }
    } catch (error) {
      console.warn("Sales details endpoint unavailable:", error);
      setSalesDetails([]);
      setDetailsUnavailable(true);
    }
  };

  useEffect(() => {
    getSalesDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCategory, matchedCategories, selectedYear]);

  
  const filteredDetailRows = useMemo(() => {
    if (!effectiveMonth) return salesDetails;
    return salesDetails.filter((row) => {
      const rowMonth = moment(row?.order_month, "DD-MM-YYYY");
      return rowMonth.isValid() && rowMonth.format("YYYY-MM") === effectiveMonth;
    });
  }, [salesDetails, effectiveMonth]);

 
  useEffect(() => {
    setExpandedRowKey(null);
    setExpandedRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDetailRows]);


  const handleBarClick = (category, datum) => {
    const month = datum?.month;
    if (!month) return;

    setChartClick((prev) =>
      prev && prev.category === category && prev.month === month ? null : { category, month }
    );
  };

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value, 10));
  };

  const financialYearLabel = `FY ${selectedYear}-${String(selectedYear + 1).slice(-2)}`;

  
  // INVOICE EXPAND/COLLAPSE — 

  const handleInvoiceClick = async (row, idx) => {
    const poId = row?.id ?? row?.po_id;
    const key = `${poId}-${row?.product_id}-${idx}`;

    // Clicking the same invoice again closes it.
    if (expandedRowKey === key) {
      setExpandedRowKey(null);
      setExpandedRows([]);
      return;
    }

    if (poId == null) {
      toast.error("Missing order id for this row — can't load invoice details.");
      return;
    }

    setExpandedRowKey(key);
    setExpandedRows([]);
    setExpandedLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("po_id", poId);
     
      if (row?.product_id != null) params.append("exclude_product", row.product_id);

      const response = await getData(`/admin/dashboard/invoice-details?${params.toString()}`);
      if (response?.success) {
       
        const clickedProductId = row?.product_id;
        const clickedProductName = String(row?.Product || "").trim().toLowerCase();

        const deduped = toArray(response?.data).filter((sub) => {
          if (clickedProductId != null && sub?.product_id != null) {
            return sub.product_id !== clickedProductId;
          }
          return String(sub?.Product || "").trim().toLowerCase() !== clickedProductName;
        });

        setExpandedRows(deduped);
      } else {
        setExpandedRows([]);
        toast.error("Couldn't load other products for this invoice.");
      }
    } catch (error) {
      console.warn("Invoice details endpoint failed:", error);
      setExpandedRows([]);
      toast.error("Couldn't load other products for this invoice.");
    } finally {
      setExpandedLoading(false);
    }
  };

  

  return (
    <>
      <div className="main-advancedashboard">
        <Header title="Sales Analytics" link="/sales-analytics" />

        <section className="AdvanceDashboard">
          <div className="col-xxl-12 col-xl-12 p-0">
            <div className="row MainRowsec me-0 ms-0">
              <section className="Tabels tab-radio">
                <div>
                  {/* ================================================================
                      CATEGORY FILTER CARDS —
                  ================================================================ */}
                  <Row className="mb-3 g-2">
                    {CATEGORIES.map((label) => {
                      const isActive = selectedCategory === label;
                      const total = categoryYearTotals.find((c) => c.label === label)?.total;
                      return (
                        <Col lg={3} md={6} sm={6} xs={6} key={label}>
                          <button
                            type="button"
                            className={`category-filter-card${isActive ? " active" : ""}`}
                            onClick={() => handleCategoryCardClick(label)}
                            title={summaryUnavailable ? "Sales summary endpoint unavailable" : undefined}
                          >
                            <div className="cat-label">{label}</div>
                            <div className="cat-count">{summaryUnavailable ? "—" : total}</div>
                          </button>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* ================================================================
                      CHART — 
                  ================================================================ */}
                  <Row>
                    <Col lg={12} md={12} className="mb-4">
                      {summaryUnavailable ? (
                        <div className="sales-chart-panel sales-chart-panel--unavailable p-3 d-flex align-items-center justify-content-center">
                          <p className="no-datashow text-center mb-0">
                            Sales summary endpoint isn't reachable.
                            <br />
                            Confirm <code>/admin/dashboard/sales-summary</code> is wired up on the backend.
                          </p>
                        </div>
                      ) : (
                        <div className="sales-chart-panel p-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <h5 className="mb-0">Total Sales by Category</h5>
                            <Form.Select
                              size="sm"
                              value={selectedYear}
                              onChange={handleYearChange}
                              style={{ width: 170 }}
                              aria-label="Select financial year"
                            >
                              {yearOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 60 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="label"
                                tick={{ fontSize: 10 }}
                                interval={0}
                                angle={-45}
                                textAnchor="end"
                                height={70}
                              />
                              <YAxis allowDecimals={false} />
                              <Tooltip formatter={(value, name) => [value, name]} />
                              <Legend />
                              {chartCategories.map((cat) => (
                                <Bar
                                  key={cat}
                                  dataKey={cat}
                                  name={cat}
                                  barSize={14}
                                  minPointSize={2}
                                  cursor="pointer"
                                  fill={
                                    highlightCategory && highlightCategory !== cat
                                      ? GRAY_COLOR
                                      : CATEGORY_COLORS[cat] || "#4C6EF5"
                                  }
                                  onClick={(datum) => handleBarClick(cat, datum)}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>

                          {salesSummary.length === 0 && (
                            <p className="text-muted text-center mt-2 mb-0">
                              No sales records were returned for {financialYearLabel}. If you expect data
                              here, verify that <code>product_orders</code> rows exist in this date range
                              and that the category id the page is sending matches the{" "}
                              <code>p_category_id</code> your products actually use.
                            </p>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>

                  {/* ================================================================
                      SALES DETAILS TABLE — 
                  ================================================================ */}
                  <div className="mt-2">
                    <h5 className="mb-3">
                      Sales Details — {financialYearLabel}
                      {effectiveCategory
                        ? ` — ${effectiveCategory}${
                            effectiveMonth ? ` (${moment(effectiveMonth, "YYYY-MM").format("MMM YYYY")})` : ""
                          }`
                        : " (Summary — no category selected)"}
                    </h5>

                    {detailsUnavailable ? (
                      <p className="no-datashow">
                        Sales details endpoint isn't reachable. Confirm{" "}
                        <code>/admin/dashboard/sales-details</code> is wired up on the backend.
                      </p>
                    ) : effectiveCategory ? (
                      <Table bordered hover responsive size="sm">
                        <thead>
                          <tr>
                            <th title="Order date">Date</th>
                            <th title="Invoice number — click to see other products on this invoice">
                              Invoice No.
                            </th>
                            <th title="Customer name">Customer</th>
                            <th title="Product name">Product</th>
                            <th title="Brand name">Brand</th>
                            <th title="Quantity sold">Quantity</th>
                            <th title="This product's value on the invoice">Price</th>
                            <th title="Total value of the whole invoice">Order Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDetailRows.length > 0 ? (
                            filteredDetailRows.map((row, idx) => {
                              const poId = row?.id ?? row?.po_id;
                              const key = `${poId}-${row?.product_id}-${idx}`;
                              const isExpanded = expandedRowKey === key;

                              return (
                                <React.Fragment key={key}>
                                  <tr>
                                    <td title={row?.order_month}>{row?.order_month}</td>
                                    <td
                                      className="invoice-link"
                                      title="Click to see other products on this invoice"
                                      onClick={() => handleInvoiceClick(row, idx)}
                                    >
                                      {row?.invoice_no}
                                    </td>
                                    <td title={row?.Customer}>{row?.Customer}</td>
                                    <td title={row?.Product}>{row?.Product}</td>
                                    <td title={row?.Brands}>{row?.Brands}</td>
                                    <td title={row?.total_quantity}>{row?.total_quantity}</td>
                                    <td title={row?.Amount}>{row?.Amount}</td>
                                    <td title={row?.["Invoice Amount"]}>{row?.["Invoice Amount"]}</td>
                                  </tr>

                                  {isExpanded && expandedLoading && (
                                    <tr className="invoice-subrow">
                                      <td colSpan={8} className="text-center">
                                        Loading other products…
                                      </td>
                                    </tr>
                                  )}

                                  {isExpanded && !expandedLoading && expandedRows.length === 0 && (
                                    <tr className="invoice-subrow">
                                      <td colSpan={8} className="text-center">
                                        No other products on this invoice.
                                      </td>
                                    </tr>
                                  )}

                                  {isExpanded &&
                                    !expandedLoading &&
                                    expandedRows.map((sub, subIdx) => (
                                      <tr key={`${key}-sub-${subIdx}`} className="invoice-subrow">
                                        <td>—</td>
                                        <td>—</td>
                                        <td title={sub?.Customer}>{sub?.Customer}</td>
                                        <td title={sub?.Product}>{sub?.Product}</td>
                                        <td title={sub?.Brands}>{sub?.Brands}</td>
                                        <td title={sub?.total_quantity}>{sub?.total_quantity}</td>
                                        <td title={sub?.Amount}>{sub?.Amount}</td>
                                        <td>—</td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={8} className="text-center text-muted">
                                No sales details found for this selection.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    ) : (
                      // Total sales quantity per category across the selected financial year,
                      // shown when no category is selected.
                      <Table bordered size="sm" className="mb-0 sales-details-summary-table">
                        <thead>
                          <tr>
                            <th title="Category">Category</th>
                            <th title="Total quantity sold">Total Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryYearTotals.map((c) => (
                            <tr key={c.label}>
                              <td title={c.label}>{c.label}</td>
                              <td title={`${c.label} — Qty: ${c.total}`}>{c.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </section>
      </div>

      <Toaster position="top-right" />
    </>
  );
};

export default SalesAnalytics;